import { useState } from "react";

import {
  Button,
  Table,
  ButtonLink,
  Modal,
  ArrowButton,
  TableProps,
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as AddIcon } from "@king-ict/eupisi-ui/components/Icon/AddWhite.svg";
import { ReactComponent as Download } from "@king-ict/eupisi-ui/components/Icon/Download.svg";
import { RequestStatusEnum } from "@king-ict/eupisi-ui/models";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import styles from "./AdministrationList.module.scss";

export interface AdministrationListProps {
  tableProps: TableProps;
  isCreateEnabled?: boolean;
  isDeleteEnabled?: boolean;
  isDetailsEnabled?: boolean;
  isDownloadEnabled?: boolean;
  deleteAndUpdateAllowedByDate?: boolean;
  deleteActionText?: string;
  deleteModalTitle?: string;
  deleteModalMessage?: string;
  onAddNewClicked?: () => void;
  onDetailsClicked?: (id?: number, guid?: string) => void;
  onDeleteConfirmed?: (id: number) => Promise<boolean>;
  onDownloadPdfClicked?: (id: number) => void;
}

export const AdministrationList = ({
  tableProps,
  isCreateEnabled = false,
  isDeleteEnabled = false,
  isDetailsEnabled = false,
  isDownloadEnabled = false,
  deleteAndUpdateAllowedByDate = false,
  deleteActionText,
  deleteModalTitle,
  deleteModalMessage,
  onAddNewClicked,
  onDetailsClicked,
  onDeleteConfirmed,
  onDownloadPdfClicked,
}: AdministrationListProps) => {
  const [isDeleteModalOpened, setIsDeleteModalOpened] = useState(false);
  const [isMessageModalOpened, setIsMessageModalOpened] = useState(false);
  const [actionId, setActionId] = useState<number>();

  const { t } = useTranslation();

  const handleToggleDeleteModal = () => {
    setIsDeleteModalOpened((prev) => !prev);
  };

  const handleToggleMessageModal = () => {
    setIsMessageModalOpened((prev) => !prev);
  };

  const handleOpenDeleteModal = (id: number) => {
    handleToggleDeleteModal();
    setActionId(id);
  };

  const handleDeleteConfirm = async () => {
    handleToggleDeleteModal();

    if (
      actionId &&
      isDeleteEnabled &&
      onDeleteConfirmed &&
      (await onDeleteConfirmed(actionId))
    ) {
      handleToggleMessageModal();
    }
  };

  const handleDetailsClicked = (id?: number, guid?: string) => {
    if (onDetailsClicked) {
      onDetailsClicked(id, guid);
    }
  };

  const handleDownloadPdfClicked = (id: number) => {
    if (onDownloadPdfClicked) {
      onDownloadPdfClicked(id);
    }
  };

  const resolveTableProps = () => {
    const newMetadata = {
      ...tableProps.metadata,
      columns: [...tableProps.metadata.columns],
    };

    if (isDeleteEnabled) {
      newMetadata.columns.push({
        columnKey: "delete",
        header: "",
        accessor: (d: any) => (
          <>
            {
              // deleteAndUpdateAllowedByDate added as fast solution, make criteria which added in regular administration event cant be deleted. Remove this after some better implementation
              deleteAndUpdateAllowedByDate ? (
                new Date(2021, 8, 28, 0, 0, 0) >
                new Date(d.dateCreated) ? null : (
                  <ButtonLink
                    onClick={() => handleOpenDeleteModal(d[newMetadata.rowKey])}
                  >
                    {deleteActionText}
                  </ButtonLink>
                )
              ) : (
                <ButtonLink
                  onClick={() => handleOpenDeleteModal(d[newMetadata.rowKey])}
                >
                  {deleteActionText}
                </ButtonLink>
              )
            }
          </>
        ),
      });
    }

    if (isDownloadEnabled) {
      newMetadata.columns.push({
        columnKey: "download",
        header: "",
        accessor: (d: any) =>
          d.requestStatusId !== RequestStatusEnum.Draft && (
            <div className={styles.wrapper}>
              <button
                className={`${styles.arrowButton}`}
                onClick={() => handleDownloadPdfClicked(d[newMetadata.rowKey])}
              >
                <Download />
              </button>
            </div>
          ),
      });
    }

    if (isDetailsEnabled) {
      newMetadata.columns.push({
        columnKey: "details",
        header: "",
        accessor: (d: any) => (
          <>
            {
              // deleteAndUpdateAllowedByDate added as fast solution, make criteria which added in regular administration event cant be deleted and updated. Remove this after some better implementation
              deleteAndUpdateAllowedByDate ? (
                new Date(2021, 8, 28, 0, 0, 0) >
                new Date(d.dateCreated) ? null : (
                  <div className={styles.detailsBtnRight}>
                    <ArrowButton
                      ariaLabel={t("accessibility.backButton")}
                      onClick={() =>
                        handleDetailsClicked(d[newMetadata.rowKey], d["guid"])
                      }
                    />
                  </div>
                )
              ) : (
                <div className={styles.detailsBtnRight}>
                  <ArrowButton
                    ariaLabel={t("accessibility.backButton")}
                    onClick={() =>
                      handleDetailsClicked(d[newMetadata.rowKey], d["guid"])
                    }
                  />
                </div>
              )
            }
          </>
        ),
      });
    }

    return { ...tableProps, metadata: newMetadata } as TableProps;
  };

  return (
    <>
      <Row>
        {isCreateEnabled && (
          <Col className={styles.actionsContainer}>
            <Button
              color="primary"
              icon={<AddIcon />}
              className={styles.addNewButton}
              onClick={onAddNewClicked}
            >
              {t("common.addNew")}
            </Button>
          </Col>
        )}
        <Col xs={12}>
          <Table {...resolveTableProps()}></Table>
        </Col>
      </Row>

      <Modal
        type={"confirm"}
        title={deleteModalTitle}
        cancelText={t("common.no")}
        confirmText={t("common.yes")}
        open={isDeleteModalOpened}
        onClose={handleToggleDeleteModal}
        onConfirm={handleDeleteConfirm}
      >
        {deleteModalMessage}
      </Modal>

      <Modal
        type={"message"}
        open={isMessageModalOpened}
        confirmText={t("common.close")}
        onConfirm={handleToggleMessageModal}
        onClose={handleToggleMessageModal}
      >
        {t("common.deleteSuccess")}
      </Modal>
    </>
  );
};
